// app/components/native-tapioca-starch-manufacturing-process/ProcessIcons.tsx
// Hand-drawn line-icon set (24x24 grid, Feather/Heroicons-style stroke weight)
// — one icon per manufacturing stage, in the exact order of ManufacturingProcess.steps.
import * as React from "react";

type IconProps = {
  size?: number;
  className?: string;
};

const base = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

// 1. Fresh Cassava Roots — elongated tuber tapering to a point, with a leaf sprout
export function RootIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <path d="M12 2v3" />
      <path d="M8.8 4c1 .9 1.6 1.8 1.8 3M15.2 4c-1 .9-1.6 1.8-1.8 3" />
      <path d="M12 8c-2.4 0-4 1.9-4 4.5 0 1.8.6 2.8 1.1 4.1.6 1.7 1.3 3.5 2.5 5.1.2.3.6.3.8 0 1.2-1.6 1.9-3.4 2.5-5.1.5-1.3 1.1-2.3 1.1-4.1 0-2.6-1.6-4.5-4-4.5Z" />
    </svg>
  );
}

// 2. Root Hopper — intake funnel
export function HopperIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <path d="M3 4h18l-6.5 8v6a1.5 1.5 0 0 1-3 0v-6L3 4Z" />
    </svg>
  );
}

// 3. Root Siever — circular sieve mesh
export function SieverIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <circle cx="12" cy="12" r="9" />
      <path d="M6 9h12M6 12h12M6 15h12" opacity={0.85} />
      <path d="M9 6v12M12 6v12M15 6v12" opacity={0.85} />
    </svg>
  );
}

// 4. Pre-Washing — water droplet
export function DropletIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <path d="M12 2.5S5.5 11 5.5 15a6.5 6.5 0 0 0 13 0c0-4-6.5-12.5-6.5-12.5Z" />
    </svg>
  );
}

// 5 & 7. Stone Catcher — waves over a settling stone
export function StoneCatcherIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <path d="M2 8c1.7-1.6 3.3-1.6 5 0s3.3 1.6 5 0 3.3-1.6 5 0 3.3 1.6 5 0" />
      <path d="M2 12c1.7-1.6 3.3-1.6 5 0s3.3 1.6 5 0 3.3-1.6 5 0 3.3 1.6 5 0" opacity={0.55} />
      <path d="M9.5 15.5 12 13l2.7 1 1.3 2.6-1 2.6-3.5 1-2.5-1.8Z" />
    </svg>
  );
}

// 6. Washing — rotating drum (circle + rotation arrow, classic "cycle" glyph)
export function DrumIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4c3.5 0 6.5 2.2 7.6 5.3" />
      <path d="M20.2 6.4 19.6 9.3 16.7 8.6" />
    </svg>
  );
}

// 8. Root Chopper — knife (handle + wedge blade), drawn horizontally then
// rotated diagonal — avoids the circle+diagonal-line "prohibited" collision
export function ChopperIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <g transform="rotate(45 12 12)">
        <rect x="3" y="10" width="5" height="4" rx="1" />
        <path d="M8 10h9c1.8 0 2.8 1 2.8 2s-1 2-2.8 2H8Z" />
      </g>
    </svg>
  );
}

// 9. Rasper — grater texture
export function RasperIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <rect x="4" y="4" width="16" height="16" rx="2.5" />
      <path d="M7 17 17 7M7 13 13 7M11 17 17 11" opacity={0.85} />
    </svg>
  );
}

// 10. Coarse Extractor — separating starch (down) from fiber (side)
export function CoarseExtractorIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <path d="M4 5h16l-5.2 6.5v3.2" />
      <circle cx="7" cy="6" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="17" cy="6" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="7.5" r="0.9" fill="currentColor" stroke="none" />
      <path d="M14.8 15.5v3M12.5 20h4.6" />
      <path d="M17 20l3-3M20 20l-3-3" opacity={0.85} />
    </svg>
  );
}

// 11. Concentration Hydrocyclone — spiral cyclone cone
export function HydrocycloneIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <path d="M4 4h16l-5 8.5v6.7l-1.6.9v-7.6L4 4Z" />
      <path d="M9.5 6.5c1.6.9 2.4 2.3 1.6 3.7" opacity={0.85} />
    </svg>
  );
}

// 12. Fine Extractor — finer mesh separation
export function FineExtractorIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <path d="M4 6h16l-5.2 6.8v6.7" />
      <path d="M7.5 5.6h9" opacity={0.6} />
      {[6.5, 9, 11.5, 14, 16.5].map((x) => (
        <circle key={x} cx={x} cy={4.4} r="0.65" fill="currentColor" stroke="none" />
      ))}
    </svg>
  );
}

// 13. Flash Dryer — heat/sun over rising steam
export function DryerIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M12 2.3v1.4M12 12.3v1.4M6.3 8h1.4M16.3 8h1.4M8.1 4.1l1 1M14.9 4.1l-1 1M8.1 11.9l1-1M14.9 11.9l-1-1" />
      <path d="M6 18c1.2-1.3 1.2-2.7 0-4M12 18c1.2-1.3 1.2-2.7 0-4M18 18c1.2-1.3 1.2-2.7 0-4" opacity={0.85} />
    </svg>
  );
}

// 14. Packing — sealed starch bag
export function PackingIcon({ size = 26, className }: IconProps) {
  return (
    <svg width={size} height={size} className={className} {...base}>
      <path d="M9.5 2.5h5l.8 6h-6.6l.8-6Z" />
      <path d="M8 8.5h8l1.6 11a1.5 1.5 0 0 1-1.5 1.7H7.9a1.5 1.5 0 0 1-1.5-1.7l1.6-11Z" />
      <path d="M9.3 12.5h5.4" opacity={0.7} />
    </svg>
  );
}

export const PROCESS_ICONS = [
  RootIcon,
  HopperIcon,
  SieverIcon,
  DropletIcon,
  StoneCatcherIcon,
  DrumIcon,
  StoneCatcherIcon,
  ChopperIcon,
  RasperIcon,
  CoarseExtractorIcon,
  HydrocycloneIcon,
  FineExtractorIcon,
  DryerIcon,
  PackingIcon,
] as const;
